import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Save, RefreshCw, Trash2 } from "lucide-react";
import { Logo } from "../components/site/Logo";
import { JsonField } from "../components/admin/JsonField";
import { http, apiError } from "../lib/api";
import { useContent, usePageMeta } from "../lib/content";

const TOKEN = "rivanta_admin_token";
const SECTIONS = [["contact", "Contact information"], ["packages", "Pricing packages"], ["ad_spend_disclosure", "Pricing disclosure"], ["case_studies", "Case studies"], ["results_disclaimer", "Results disclaimer"], ["sms", "SMS consent disclosures"], ["legal", "Privacy & terms placeholders"]];
const STATUSES = ["new", "contacted", "qualified", "not_a_fit", "client"];

function Login({ onToken }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await http.post("/auth/login", { email, password }); localStorage.setItem(TOKEN, data.token); onToken(data.token); }
    catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-24 max-w-sm space-y-4 rounded-3xl border border-white/10 bg-panel p-8" data-testid="admin-login-form">
      <h1 className="text-2xl font-semibold text-white">Admin sign in</h1>
      <label className="block text-sm text-slate-300">Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="field mt-1.5" data-testid="admin-email-input" autoComplete="username" /></label>
      <label className="block text-sm text-slate-300">Password<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="field mt-1.5" data-testid="admin-password-input" autoComplete="current-password" /></label>
      {err && <p role="alert" className="text-sm text-red-300" data-testid="admin-login-error">{err}</p>}
      <button className="btn-primary w-full" disabled={busy} data-testid="admin-login-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in</button>
    </form>
  );
}

function Leads({ auth }) {
  const [leads, setLeads] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await http.get("/admin/leads", auth); setLeads(Array.isArray(data) ? data : []); } catch (e) { toast.error(apiError(e)); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await http.patch(`/admin/leads/${id}`, { status }, auth); load(); };
  const remove = async (id) => { if (window.confirm("Delete this lead permanently?")) { await http.delete(`/admin/leads/${id}`, auth); load(); } };
  if (!leads) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!leads.length) return <p className="text-slate-400" data-testid="admin-leads-empty">No applications yet.</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-leads-table">
      <table className="w-full min-w-[1000px] text-left text-sm">
        <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-slate-400">
          <tr>{["Date", "Name", "Business", "Contact", "Revenue", "Ad spend", "Package", "SMS consent", "Notifications", "Status", ""].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-t border-white/[0.06] align-top text-slate-200">
              <td className="px-3 py-3 text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</td>
              <td className="px-3 py-3 font-medium text-white">{l.first_name} {l.last_name}</td>
              <td className="px-3 py-3">{l.business_name}<br /><span className="text-xs text-slate-400">{l.industry}</span></td>
              <td className="px-3 py-3"><a href={`tel:${l.phone}`} className="underline">{l.phone}</a><br /><a href={`mailto:${l.email}`} className="text-xs text-slate-400 underline">{l.email}</a></td>
              <td className="px-3 py-3">{l.revenue_range}</td>
              <td className="px-3 py-3">{l.ad_spend || "Not running ads"}</td>
              <td className="px-3 py-3">{l.package}</td>
              <td className="px-3 py-3 text-xs">{l.sms_consent ? `Yes · ${new Date(l.sms_consent_record?.timestamp).toLocaleDateString()}` : "No"}{l.sms_suppressed && <span className="block text-red-300">Opted out (STOP)</span>}</td>
              <td className="px-3 py-3 text-xs text-slate-400">{Object.entries(l.notifications || {}).map(([k, v]) => <div key={k}>{k}: {v?.status || String(v)}</div>)}</td>
              <td className="px-3 py-3">
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="rounded-md border border-white/10 bg-ink px-2 py-1 text-xs" data-testid={`admin-lead-status-${l.id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td className="px-3 py-3"><button onClick={() => remove(l.id)} aria-label="Delete lead" className="text-slate-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContentEditor({ token, auth }) {
  const { content, setContent } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (content && !draft) setDraft(content); }, [content, draft]);
  if (!draft) return <Loader2 className="h-5 w-5 animate-spin" />;
  const save = async () => {
    setSaving(true);
    try { const { data } = await http.put("/admin/content", draft, auth); setContent(data); setDraft(data); toast.success("Content saved — live on the site"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6">
      {SECTIONS.map(([key, title]) => (
        <section key={key} className="rounded-2xl border border-white/10 bg-panel/60 p-5 sm:p-6">
          <h2 className="mb-4 text-lg font-semibold text-white">{title}</h2>
          <JsonField name={typeof draft[key] === "string" ? key : ""} value={draft[key]} token={token} path={key} onChange={(v) => setDraft({ ...draft, [key]: v })} />
        </section>
      ))}
      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="admin-save-content-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes</button>
      </div>
    </div>
  );
}

function Integrations({ auth }) {
  const [s, setS] = useState(null);
  useEffect(() => { http.get("/admin/integrations", auth).then((r) => setS(r.data)).catch(() => setS({})); }, [auth]);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;
  const rows = [
    ["Phone push alerts (ntfy app)", s.phone_push, `Install the free ntfy app and subscribe to the topic "${s.ntfy_topic || "NTFY_TOPIC"}". Each new lead sends an instant notification (first name, industry, package only); tap it to open this admin.`],
    ["Lead SMS alerts (Twilio)", s.twilio_sms, `Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER in backend env. Alerts go to ${s.agency_notify_phone || "AGENCY_NOTIFY_PHONE"}. Point your Twilio number's inbound webhook to /api/sms/inbound for STOP/HELP handling.`],
    ["CRM webhook (GoHighLevel / Zapier / Make)", s.crm_webhook, "Set CRM_WEBHOOK_URL (and optional CRM_WEBHOOK_SECRET for an HMAC X-Rivanta-Signature header). Every new lead is POSTed as JSON."],
    ["Meta Conversions API", s.meta_capi, "Set META_PIXEL_ID and META_CAPI_ACCESS_TOKEN in backend env. Lead events share an event ID with the Pixel for de-duplication."],
    ["GA4 + Meta Pixel (browser)", Boolean(process.env.REACT_APP_GA4_ID || process.env.REACT_APP_META_PIXEL_ID), "Set REACT_APP_GA4_ID / REACT_APP_META_PIXEL_ID in frontend env. They load only after a visitor accepts the consent banner."],
    ["Media storage", s.object_storage, "Used for case-study videos and screenshots."],
  ];
  return (
    <ul className="space-y-3" data-testid="admin-integrations">
      {rows.map(([name, on, help]) => (
        <li key={name} className="rounded-2xl border border-white/10 bg-panel/60 p-5">
          <div className="flex items-center justify-between gap-3"><p className="font-semibold text-white">{name}</p>
            <span className={`rounded-full px-3 py-1 text-xs ${on ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-300/10 text-amber-200"}`}>{on ? "Connected" : "Not configured"}</span></div>
          <p className="mt-2 text-sm text-slate-400">{help}</p>
        </li>
      ))}
    </ul>
  );
}

export default function Admin() {
  usePageMeta("Admin | Rivanta Labs", "");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN));
  const [tab, setTab] = useState("leads");
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  useEffect(() => {
    if (!token) return;
    http.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } }).catch(() => { localStorage.removeItem(TOKEN); setToken(null); });
  }, [token]);
  const logout = () => { localStorage.removeItem(TOKEN); setToken(null); };

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/[0.07]"><div className="container-x flex h-16 items-center justify-between"><Logo testId="admin-logo-link" />
        {token && <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>}</div></header>
      {!token ? <Login onToken={setToken} /> : (
        <main className="container-x py-10">
          <div className="flex flex-wrap items-center gap-2">
            {[["leads", "Applications"], ["content", "Site content"], ["integrations", "Integrations"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`rounded-full px-4 py-2 text-sm ${tab === k ? "bg-electric text-white" : "border border-white/10 text-slate-300"}`}>{l}</button>
            ))}
            <Link to="/" className="ml-auto flex items-center gap-1 text-sm text-slate-400 hover:text-white"><RefreshCw className="h-3.5 w-3.5" /> View site</Link>
          </div>
          <div className="mt-8">
            {tab === "leads" && <Leads auth={auth} />}
            {tab === "content" && <ContentEditor token={token} auth={auth} />}
            {tab === "integrations" && <Integrations auth={auth} />}
          </div>
        </main>
      )}
    </div>
  );
}
